import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import logo from "../../assets/images/Logo.png"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import { faAngleLeft, faBars, faSearch, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { useLocation, useNavigate } from "react-router-dom"
import * as signalR from "@microsoft/signalr"
import { PATH_MAP } from "../../configuration/PathMap"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FloatButton } from "../FloatButton/FloatButton"
import { ChangeLanguage } from "../ChangeLanguage/ChangeLanguage"
import { useSystemMode } from "../../context/useSystemModeContext"
import { companyMemory } from "../../Objects/Memory/Memory"
import { EKitchenBarMode } from "../../Enum/EKitchenBarMode"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { getRestaurantNow } from "../../utils/timeZone"
import { useTranslation } from "react-i18next"
import type { OperatingScheduleEntity } from "../../Objects/OperatingSchedule/OperatingScheduleEntity"
import { SystemFloatingAlerts, type SystemFloatingAlertItem } from "../SystemFloatingAlerts/SystemFloatingAlerts"
import { Permissions } from "../../Enum/PermissionsType"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import {
  type OrderKitchenBarCounters,
  useGetOrderKitchenBarCounters,
} from "../../hooks/Order/useGetOrderKitchenBarCounters"
import { useWhatsAppAutomation } from "../../hooks/WhatsAppAutomation/useWhatsAppAutomation"
import { QuickSearchPopup } from "../QuickSearch/QuickSearchPopup"
import { getDateRangeSearch } from "../../utils/dateRangeSearchParams"
import { PlatformNoticeBell } from "../PlatformNotice/PlatformNoticeBell"
import { OperationalHelpFloatingButton } from "../OperationalHelp/OperationalHelpFloatingButton"
import { EOperationalHelpSurface } from "../../Objects/OperationalHelp/EOperationalHelpSurface"
import type { OperationalHelpAction } from "../../Objects/OperationalHelp/OperationalHelpAction"
import { ERequestType } from "../../Enum/ERequestType"
import { useCompanyModulesStatus } from "../../context/useCompanyModulesStatusContext"
import { getAvailableQuickSearchActions } from "../../utils/quickSearchActions"
import { buildQuickSearchActionSearch } from "../../utils/quickSearchNavigation"

interface MenuLoggedProp {
  enableButtons?: boolean
}

type WhatsAppHumanHandoffAlert = {
  agentId: number
  remotePhone: string
  remoteName?: string | null
  body: string
  matchedKeyword: string
  triggeredAt: string
}

const WHATSAPP_AGENT_STATUS_POLLING_MS = 180000

const normalizeOperationalHelpKeywords = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []

const parseTimeToMinutes = (value?: string | null): number | null => {
  const [hoursRaw, minutesRaw] = value?.split(":") ?? []
  const hours = Number(hoursRaw)
  const minutes = Number(minutesRaw)

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }

  return hours * 60 + minutes
}

const isRestaurantOpenNow = (schedules: OperatingScheduleEntity[] | undefined, date: Date): boolean => {
  if (!schedules || schedules.length === 0) {
    return false
  }

  const currentDay = date.getDay()

  const previousDay = currentDay === 0 ? 6 : currentDay - 1

  const nowMinutes = date.getHours() * 60 + date.getMinutes()

  return schedules.some((schedule) => {
    if (schedule.dayEnable === false) {
      return false
    }

    const openMinutes = parseTimeToMinutes(schedule.openTime)

    const closeMinutes = parseTimeToMinutes(schedule.closeTime)

    if (openMinutes === null || closeMinutes === null) {
      return false
    }

    if (openMinutes === closeMinutes) {
      return schedule.day === currentDay
    }

    if (schedule.day === currentDay) {
      return openMinutes < closeMinutes
        ? nowMinutes >= openMinutes && nowMinutes < closeMinutes
        : nowMinutes >= openMinutes
    }

    return schedule.day === previousDay && openMinutes > closeMinutes && nowMinutes < closeMinutes
  })
}

const MenuLogged: React.FC<MenuLoggedProp> = ({ enableButtons = true }) => {
  const { t } = useTranslation("frontend")

  const { t: tQuickSearch } = useTranslation("quickSearch")

  const { handleChangeStatusMenu, handleForceCloseMenu, isNotMobile } = useMenuMobileContext()

  const { systemMode, setSystemMode } = useSystemMode()

  const { userEmployee } = useEmployerLogged()

  const { isModuleEnabled } = useCompanyModulesStatus()

  const { handleGetOrderKitchenBarCounters } = useGetOrderKitchenBarCounters()

  const { getOverview } = useWhatsAppAutomation()

  const [kitchenOpenOrdersCount, setKitchenOpenOrdersCount] = useState(0)

  const [barOpenOrdersCount, setBarOpenOrdersCount] = useState(0)

  const [delayedOrdersCount, setDelayedOrdersCount] = useState(0)

  const [isDelayedWarningDismissed, setIsDelayedWarningDismissed] = useState(false)

  const [isWhatsAppOfflineWarningDismissed, setIsWhatsAppOfflineWarningDismissed] = useState(false)

  const [shouldShowWhatsAppOfflineWarning, setShouldShowWhatsAppOfflineWarning] = useState(false)

  const [humanHandoffAlerts, setHumanHandoffAlerts] = useState<WhatsAppHumanHandoffAlert[]>([])

  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false)
  const [isOperationalHelpOpen, setIsOperationalHelpOpen] = useState(false)

  const isFetchingOrderCountersRef = useRef(false)

  const isFetchingWhatsAppStatusRef = useRef(false)

  const canReceiveWhatsAppAlerts =
    userEmployee?.isMaster || userEmployee?.permissions?.includes(Permissions.CanAnswerWhatsApp)

  const navigate = useNavigate()

  const location = useLocation()

  const isFirstPage = location.pathname === PATH_MAP.COMMANDS_PAGE

  const availableQuickSearchActions = useMemo(
    () => getAvailableQuickSearchActions(userEmployee, isModuleEnabled),
    [isModuleEnabled, userEmployee],
  )

  const operationalHelpActions = useMemo<OperationalHelpAction[]>(
    () =>
      availableQuickSearchActions.map((action) => ({
        id: action.id,
        label: tQuickSearch(`actions.${action.id}.title`),
        description: tQuickSearch(`actions.${action.id}.description`),
        group: action.group,
        type: action.type,
        path: action.path,
        searchParams: action.searchParams,
        keywords: normalizeOperationalHelpKeywords(
          tQuickSearch(`actions.${action.id}.keywords`, { returnObjects: true }),
        ),
      })),
    [availableQuickSearchActions, tQuickSearch],
  )

  useEffect(() => {
    if (location.pathname.startsWith(PATH_MAP.KITCHEN_PAGE) || location.pathname.startsWith(PATH_MAP.BAR_PAGE)) {
      if (systemMode !== "KITCHEN") {
        setSystemMode("KITCHEN")
      }
    } else {
      if (systemMode !== "STAFF") {
        setSystemMode("STAFF")
      }
    }
  }, [location.pathname])

  const handleSetKitchen = () => {
    setSystemMode("KITCHEN")

    navigate(`${PATH_MAP.KITCHEN_PAGE}${getDateRangeSearch(window.location.search || location.search)}`)
  }

  const handleSetStaff = () => {
    navigate(`${PATH_MAP.COMMANDS_PAGE}${getDateRangeSearch(window.location.search || location.search)}`)

    setSystemMode("STAFF")
  }

  const handleSetBar = () => {
    setSystemMode("KITCHEN")

    navigate(`${PATH_MAP.BAR_PAGE}${getDateRangeSearch(window.location.search || location.search)}`)
  }

  const hasSeparatedBar = companyMemory.kitchenBarMode === EKitchenBarMode.Separated

  const updateOrderCounters = useCallback((counters: OrderKitchenBarCounters) => {
    setKitchenOpenOrdersCount(counters.kitchenOpenOrdersCount)
    setBarOpenOrdersCount(counters.barOpenOrdersCount)
    setDelayedOrdersCount(counters.delayedOpenOrdersCount)
    setIsDelayedWarningDismissed(false)
  }, [])

  const fetchOrderCounters = useCallback(async () => {
    if (isFetchingOrderCountersRef.current) {
      return
    }

    isFetchingOrderCountersRef.current = true

    try {
      const counters = await handleGetOrderKitchenBarCounters()

      updateOrderCounters(counters)
    } catch {
      return
    } finally {
      isFetchingOrderCountersRef.current = false
    }
  }, [handleGetOrderKitchenBarCounters, updateOrderCounters])

  useEffect(() => {
    fetchOrderCounters()

    const interval = setInterval(fetchOrderCounters, 300000)

    return () => clearInterval(interval)
  }, [fetchOrderCounters, updateOrderCounters])

  const fetchWhatsAppAgentStatus = useCallback(async () => {
    if (isFetchingWhatsAppStatusRef.current) {
      return
    }

    isFetchingWhatsAppStatusRef.current = true

    try {
      const restaurantNow = getRestaurantNow(companyMemory.timeZoneId)

      const isBusinessOpen = isRestaurantOpenNow(companyMemory.operatingSchedules, restaurantNow)

      if (!isBusinessOpen) {
        setShouldShowWhatsAppOfflineWarning(false)
        setIsWhatsAppOfflineWarningDismissed(false)
        return
      }

      const agents = await getOverview(true)

      const linkedAgents = agents.filter((agent) => !agent.revokedAt && !agent.hasPendingPairing)

      const hasLinkedAgent = linkedAgents.length > 0

      const hasReadyAgent = linkedAgents.some((agent) => agent.isOnline && agent.isWhatsAppReady)

      const shouldAlert = hasLinkedAgent && !hasReadyAgent

      setShouldShowWhatsAppOfflineWarning(shouldAlert)

      if (!shouldAlert) {
        setIsWhatsAppOfflineWarningDismissed(false)
      }
    } catch {
      setShouldShowWhatsAppOfflineWarning(false)
    } finally {
      isFetchingWhatsAppStatusRef.current = false
    }
  }, [])

  useEffect(() => {
    fetchWhatsAppAgentStatus()

    const interval = setInterval(fetchWhatsAppAgentStatus, WHATSAPP_AGENT_STATUS_POLLING_MS)

    return () => clearInterval(interval)
  }, [fetchWhatsAppAgentStatus])

  useEffect(() => {
    if (!canReceiveWhatsAppAlerts) {
      setHumanHandoffAlerts([])
      return
    }

    const token = localStorage.getItem(API_ROUTES.TOKEN)?.replace(/['"]/g, "")

    if (!token) {
      return
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(API_ROUTES.HUB.WHATSAPP_PANEL, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build()

    connection.on("ReceiveHumanHandoffAlert", (alert: WhatsAppHumanHandoffAlert) => {
      setHumanHandoffAlerts((current) => {
        const withoutSameConversation = current.filter((item) => item.remotePhone !== alert.remotePhone)

        return [alert, ...withoutSameConversation].slice(0, 3)
      })
    })

    connection.onreconnected(() => {
      void connection.invoke("JoinCompanyGroup").catch(() => undefined)
    })

    void connection
      .start()
      .then(() => connection.invoke("JoinCompanyGroup"))
      .catch(() => undefined)

    return () => {
      void connection.stop()
    }
  }, [canReceiveWhatsAppAlerts])

  const handleGoBack = () => {
    const canUseHistory =
      typeof window !== "undefined" &&
      (() => {
        try {
          const index = (window.history.state as any)?.idx

          if (typeof index === "number") {
            return index > 0
          }

          return window.history.length > 1
        } catch {
          return false
        }
      })()

    if (canUseHistory) {
      navigate(-1)
    } else {
      navigate(PATH_MAP.COMMANDS_PAGE)
    }
  }

  const handleQuickSearchNavigate = () => {
    setIsOperationalHelpOpen(false)

    if (!isNotMobile) {
      handleForceCloseMenu()
    }
  }

  const handleOperationalHelpAction = (action: OperationalHelpAction) => {
    const quickSearchAction = availableQuickSearchActions.find((item) => item.id === action.id)

    if (!quickSearchAction) {
      return
    }

    if (!isNotMobile) {
      handleForceCloseMenu()
    }

    navigate({
      pathname: quickSearchAction.path,
      search: buildQuickSearchActionSearch(quickSearchAction, location.search),
    })
  }

  const floatingAlerts: SystemFloatingAlertItem[] = []

  if (delayedOrdersCount > 0 && !isDelayedWarningDismissed) {
    floatingAlerts.push({
      id: "delayed-orders",
      title: t("kitchen.delayed_orders_warning_title"),
      description: t("kitchen.delayed_orders_warning_description", { count: delayedOrdersCount }),
      closeLabel: t("kitchen.close_delayed_orders_warning"),
      icon: faTriangleExclamation,
      variant: "danger",
      onClose: () => setIsDelayedWarningDismissed(true),
      onClick: () => {
        const targetPath = location.pathname.startsWith(PATH_MAP.BAR_PAGE) ? PATH_MAP.BAR_PAGE : PATH_MAP.KITCHEN_PAGE

        const dateRangeSearch = getDateRangeSearch(window.location.search || location.search)

        const separator = dateRangeSearch.length > 0 ? "&" : "?"

        setSystemMode("KITCHEN")

        navigate(`${targetPath}${dateRangeSearch}${separator}showDelayed=true`)
      },
    })
  }

  if (shouldShowWhatsAppOfflineWarning && !isWhatsAppOfflineWarningDismissed) {
    floatingAlerts.push({
      id: "whatsapp-agent-offline",
      title: t("whatsapp_agent_offline_warning_title"),
      description: t("whatsapp_agent_offline_warning_description"),
      closeLabel: t("close_whatsapp_agent_offline_warning"),
      icon: faWhatsapp,
      variant: "warning",
      onClose: () => setIsWhatsAppOfflineWarningDismissed(true),
    })
  }

  humanHandoffAlerts.forEach((alert) => {
    floatingAlerts.push({
      id: `whatsapp-human-handoff-${alert.remotePhone}`,
      title: t("whatsapp_human_handoff_warning_title"),
      description: t("whatsapp_human_handoff_warning_description", {
        name: alert.remoteName || alert.remotePhone,
        keyword: alert.matchedKeyword,
      }),
      closeLabel: t("close_whatsapp_human_handoff_warning"),
      icon: faWhatsapp,
      variant: "warning",
      onClose: () =>
        setHumanHandoffAlerts((current) => current.filter((item) => item.remotePhone !== alert.remotePhone)),
      onClick: () => {
        setHumanHandoffAlerts((current) => current.filter((item) => item.remotePhone !== alert.remotePhone))
        navigate(`${PATH_MAP.WHATSAPP_CRM_PAGE}?remotePhone=${encodeURIComponent(alert.remotePhone)}`)
      },
    })
  })

  return (
    <>
      <div
        className="
        flex
        w-screen
        h-[60px]
        bg-white
        text-black
        shadow-[0_1px_10px_rgba(0,0,0,0.05)]
        z-955
        px-0
      "
      >
        <div className="w-full flex items-center px-5">
          {!isFirstPage && isNotMobile && enableButtons && (
            <Animate type={AnimationTypes.slideRightIn} duration="1.2s">
              <button
                onClick={handleGoBack}
                className="
                mr-3
                text-[30px]
                cursor-pointer
                transition-transform duration-300
                hover:scale-125
              "
                data-interaction-description-key="interactionDescriptions:components.menuLogged.menuLogged.button.handleGoBack"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
            </Animate>
          )}

          <div className="flex flex-row w-full justify-between items-center gap-2 md:gap-3">
            {isNotMobile && <img src={logo} className="w-[90px]" alt="Logo" />}

            <Animate type={AnimationTypes.fadeIn} duration="1.5s">
              <div
                className="
                flex
                p-1
                rounded-lg
                border-2 border-orange-200
                shadow-inner
                mr-2 md:mr-0
              "
              >
                <button
                  onClick={handleSetKitchen}
                  className={`
                  relative
                  px-3 py-1
                  rounded-lg
                  text-[0.625rem] md:text-xs
                  font-bold
                  transition-all duration-200
                  ${location.pathname.startsWith(PATH_MAP.KITCHEN_PAGE) ? "bg-orange-200 text-black font-extrabold" : "bg-transparent text-black"}
                `}
                  data-interaction-description-key="interactionDescriptions:components.menuLogged.menuLogged.button.handleSetKitchen"
                >
                  COZINHA
                  {kitchenOpenOrdersCount > 0 && (
                    <span
                      className="
                      absolute -top-2 -right-2 min-w-[20px] h-[20px]
                      px-1.5 flex items-center justify-center
                      text-[11px] ring-1 ring-red-800 font-bold text-white
                      bg-red-500 rounded-full shadow-md z-10
                    "
                    >
                      {kitchenOpenOrdersCount > 99 ? "99+" : kitchenOpenOrdersCount}
                    </span>
                  )}
                </button>

                {hasSeparatedBar && (
                  <button
                    onClick={handleSetBar}
                    className={`
                  relative
                  px-3 py-1
                  rounded-lg
                  text-[0.625rem] md:text-xs
                  font-bold
                  transition-all duration-200
                  ${location.pathname.startsWith(PATH_MAP.BAR_PAGE) ? "bg-orange-200 text-black font-extrabold" : "bg-transparent text-black"}
                `}
                    data-interaction-description-key="interactionDescriptions:components.menuLogged.menuLogged.button.handleSetBar"
                  >
                    BAR
                    {barOpenOrdersCount > 0 && (
                      <span
                        className="
                        absolute -top-2 -right-2 min-w-[20px] h-[20px]
                        px-1.5 flex items-center justify-center
                        text-[11px] ring-1 ring-red-800 font-bold text-white
                        bg-red-500 rounded-full shadow-md z-10
                      "
                      >
                        {barOpenOrdersCount > 99 ? "99+" : barOpenOrdersCount}
                      </span>
                    )}
                  </button>
                )}

                <button
                  onClick={handleSetStaff}
                  className={`
                  px-3 py-1
                  rounded-lg
                  text-[0.625rem] md:text-xs
                  font-bold
                  transition-all duration-200
                  ${systemMode === "STAFF" ? "bg-orange-300 text-black font-extrabold" : "bg-transparent text-black"}
                `}
                  data-interaction-description-key="interactionDescriptions:components.menuLogged.menuLogged.button.handleSetStaff"
                >
                  ATENDIMENTO
                </button>
              </div>
            </Animate>

            <Animate type={AnimationTypes.fadeIn} duration="2s">
              <div className="ml-1 flex items-center gap-2 md:ml-0">
                <button
                  type="button"
                  onClick={() => setIsQuickSearchOpen(true)}
                  aria-label={t("quickSearch:open")}
                  title={t("quickSearch:open")}
                  className="
                  flex h-10 items-center justify-center gap-2
                  rounded-lg bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600
                  px-3 text-sm font-black uppercase tracking-wide text-white
                  shadow-[0_8px_18px_rgba(37,99,235,0.28)]
                  ring-1 ring-blue-300/60 transition
                  hover:-translate-y-0.5 hover:from-sky-400 hover:via-blue-500 hover:to-indigo-500
                  hover:shadow-[0_10px_24px_rgba(37,99,235,0.38)]
                "
                  data-interaction-description-key="interactionDescriptions:components.menuLogged.menuLogged.button.open"
                >
                  <FontAwesomeIcon icon={faSearch} />
                  <span>{t("quickSearch:open")}</span>
                </button>

                {isNotMobile && <ChangeLanguage />}

                {userEmployee?.isMaster && <PlatformNoticeBell />}
              </div>
            </Animate>
          </div>
        </div>

        {!isNotMobile && enableButtons && (
          <FloatButton
            icon={faBars}
            onClick={handleChangeStatusMenu}
            gradient="linear-gradient(to right, #0ea5e9, #2563eb, #4f46e5)"
            iconColor="black"
            bottom={"15px"}
            right={"5px"}
            iconSize={"2x"}
            size={50}
            animationDuration="1.5s"
            animationTypes={AnimationTypes.slideUpIn}
            descriptionKey="interactionDescriptions:components.menuLogged.menuLogged.button.handleChangeStatusMenu"
          />
        )}

        {!isNotMobile && enableButtons && (
          <FloatButton
            icon={faAngleLeft}
            onClick={handleGoBack}
            gradient="linear-gradient(to right, #0ea5e9, #2563eb, #4f46e5)"
            iconColor="black"
            bottom={"15px"}
            left={"5px"}
            iconSize={"2x"}
            size={50}
            animationDuration="1.5s"
            animationTypes={AnimationTypes.slideUpIn}
            descriptionKey="interactionDescriptions:components.menuLogged.menuLogged.button.handleGoBack2"
          />
        )}
      </div>

      {enableButtons && (
        <OperationalHelpFloatingButton
          surface={EOperationalHelpSurface.RestaurantOwner}
          requestType={ERequestType.Restaurant}
          availableActions={operationalHelpActions}
          onAction={handleOperationalHelpAction}
          isOpen={isOperationalHelpOpen}
          onOpenChange={setIsOperationalHelpOpen}
          onOpenQuickSearch={() => setIsQuickSearchOpen(true)}
          userName={userEmployee?.personEntity?.name || userEmployee?.userName}
          bottom={
            isNotMobile
              ? "var(--operational-help-restaurant-bottom, 24px)"
              : "var(--operational-help-restaurant-bottom, 15px)"
          }
          right={
            isNotMobile
              ? "var(--operational-help-restaurant-right, 24px)"
              : "var(--operational-help-restaurant-right, 65px)"
          }
        />
      )}

      {isQuickSearchOpen && (
        <QuickSearchPopup
          onClose={() => setIsQuickSearchOpen(false)}
          onNavigate={handleQuickSearchNavigate}
          onOpenOperationalHelp={() => setIsOperationalHelpOpen(true)}
        />
      )}

      <SystemFloatingAlerts items={floatingAlerts} />
    </>
  )
}

export default MenuLogged
